import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { applyOrganizationTheme } from '@/hooks/useOrganizationTheme';
import { generateTeamName, validateLocation } from '@/lib/locationData';
import { OnboardingState } from './useOnboardingState';

export const useOnboardingActions = (state: OnboardingState, updateState: (updates: Partial<OnboardingState>) => void) => {
  const navigate = useNavigate();

  const searchOrganizations = async (query: string) => {
    if (!query.trim()) {
      updateState({ searchResults: [] });
      return;
    }

    updateState({ isSearching: true });
    try {
      console.log('Searching for organizations with query:', query);
      const { data: organizations, error } = await supabase
        .rpc('search_organizations_for_joining', { search_term: query });

      if (error) {
        console.error('Search error:', error);
        toast.error('Fehler bei der Suche: ' + error.message);
        return;
      }

      console.log('Found organizations:', organizations);
      // Map the response to include full logo URLs
      const orgsWithLogos = organizations?.map(org => ({
        ...org,
        color: org.color || (org.type === 'vodafone' ? '#E60000' : '#3B82F6'),
        logo_url: org.logo_url ? `https://puwkrtqzszjsjkxowvin.supabase.co/storage/v1/object/public/organization-logos/${org.logo_url}` : null
      })) || [];
      updateState({ searchResults: orgsWithLogos });
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Fehler bei der Suche');
    } finally {
      updateState({ isSearching: false });
    }
  };

  const validateOrganizationNameAjax = async (name: string) => {
    if (!name.trim() || name.length < 3) {
      updateState({ isNameTaken: false, isCheckingName: false });
      return;
    }

    updateState({ isCheckingName: true });
    
    try {
      const { data: isNameTaken, error } = await supabase
        .rpc('check_organization_name_exists', { org_name: name.trim() });

      if (error) {
        console.error('Name validation error:', error);
        updateState({ isNameTaken: false });
        return;
      }

      updateState({ isNameTaken: isNameTaken || false });
      
    } catch (error) {
      console.error('AJAX validation error:', error);
      updateState({ isNameTaken: false });
    } finally {
      updateState({ isCheckingName: false });
    }
  };

  const handleOrganizationSelect = (organization: any) => {
    console.log('=== ORGANIZATION SELECTED ===');
    console.log('Selected organization:', organization);
    updateState({
      selectedOrganization: organization,
      searchQuery: organization.name,
      searchResults: []
    });
    
    applyOrganizationTheme(organization);
  };

  const handleNext = () => {
    if (state.currentStep === 1) {
      if (!state.selectedAction) {
        toast.error('Bitte wählen Sie eine Option aus');
        return;
      }
      if (state.selectedAction === 'join' && !state.selectedOrganization) {
        toast.error("Bitte wählen Sie ein Team aus");
        return;
      }
      if (state.selectedAction === "create" && !state.organizationName.trim()) {
        toast.error("Bitte geben Sie einen Teamnamen ein");
        return;
      }
      if (state.selectedAction === "create" && (!state.selectedCountry || !state.selectedState || !state.selectedCity)) {
        toast.error("Bitte wählen Sie Land, Bundesland und Stadt aus");
        return;
      }
      if (state.selectedAction === "create" && !validateLocation(state.selectedCountry, state.selectedState, state.selectedCity)) {
        toast.error("Ungültige Standortauswahl");
        return;
      }
      if (state.selectedAction === "create" && state.isNameTaken) {
        toast.error("Dieser Teamname ist bereits vergeben");
        return;
      }
      updateState({ currentStep: 2 });
    } else if (state.currentStep === 2 && state.selectedAction === 'create') {
      updateState({ currentStep: 3 });
    } else if (state.currentStep === 2 && state.selectedAction === 'join') {
      navigate('/onboarding/processing', {
        state: {
          action: state.selectedAction,
          selectedOrganization: state.selectedOrganization,
          organizationName: state.organizationName,
          organizationColor: state.organizationColor,
          organizationLogo: state.organizationLogo,
          defaultTasks: state.defaultTasks,
          locationData: {
            country: state.selectedCountry,
            state: state.selectedState,
            city: state.selectedCity,
            generatedTeamName: state.generatedTeamName
          }
        },
        replace: true
      });
    } else if (state.currentStep === 3) {
      navigate('/onboarding/processing', {
        state: {
          action: state.selectedAction,
          selectedOrganization: state.selectedOrganization,
          organizationName: state.organizationName,
          organizationColor: state.organizationColor,
          organizationLogo: state.organizationLogo,
          defaultTasks: state.defaultTasks,
          locationData: {
            country: state.selectedCountry,
            state: state.selectedState,
            city: state.selectedCity,
            generatedTeamName: state.generatedTeamName
          }
        },
        replace: true
      });
    }
  };

  const handleBack = () => {
    console.log('handleBack called, currentStep:', state.currentStep);
    if (state.currentStep > 1) {
      const newStep = state.currentStep - 1;
      updateState({ currentStep: newStep });
      console.log('Setting currentStep to:', newStep);
      
      // Reset theme to default when going back
      const root = document.documentElement;
      root.style.setProperty('--primary', '217 91% 60%'); // Default blue
      root.style.setProperty('--accent', '217 91% 60%');
      root.style.setProperty('--primary-glow', '217 91% 70%');
    } else {
      console.log('Cannot go back, already on step 1');
    }
  };

  return {
    searchOrganizations,
    validateOrganizationNameAjax,
    handleOrganizationSelect,
    handleNext,
    handleBack
  };
};